import { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import educationPattern from '@/assets/education-pattern.webp';

interface ParallaxPatternProps {
  /** Vertical drift in px across the section's scroll range. */
  distance?: number;
  opacity?: number;
  /** Optional override for the pattern source (defaults to the original doodle pattern). */
  src?: string;
}

/**
 * Repeating doodle pattern that drifts slowly as the section scrolls past,
 * adding depth behind content. Static when reduced motion is preferred or on mobile.
 */
export default function ParallaxPattern({ distance = 80, opacity = 0.08, src }: ParallaxPatternProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [-distance / 2, distance / 2]);
  const still = reduceMotion || isMobile;

  return (
    <div ref={ref} className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
      <motion.div
        aria-hidden="true"
        style={{
          y: still ? 0 : y,
          opacity,
          backgroundImage: `url(${src ?? educationPattern})`,
          backgroundSize: '800px',
          backgroundRepeat: 'repeat',
        }}
        className="absolute -inset-y-24 inset-x-0"
      />
    </div>
  );
}
